import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import { isStandalone, logPwaInstall } from "../helpers/pwa_platform"

/**
 * OnboardingInstallReporter - logs a pwa.install activity the first time the
 * app is opened from the home screen, for families who installed without
 * using the welcome-modal guide's install button.
 *
 * Keys off server-recorded state (recordedValue) rather than the legacy
 * localStorage flag, so it stays correct across devices/browsers for the
 * same account.
 *
 * The banner this controller is mounted on was rendered server-side before
 * this install could have been logged, so without a refresh it stays on
 * stale progress until the next full page load. Once the log lands, this
 * re-fetches the banner in place (mirrors the modal controller's
 * #completeInstall/#syncProgress pair) so a family opening the freshly
 * installed PWA sees the "install" step complete immediately.
 *
 * Values: recorded - whether the server has already recorded a pwa.install
 *   activity for this account; when true, this controller does nothing |
 *   syncProgressUrl - endpoint that returns a Turbo Stream refreshing this
 *   banner; when absent, the sync is skipped and the next full page load
 *   still self-heals
 *
 * @example
 * <div data-controller="onboarding-install-reporter"
 *      data-onboarding-install-reporter-recorded-value="false"
 *      data-onboarding-install-reporter-sync-progress-url-value="/onboarding_welcome/sync_progress"></div>
 */
export default class extends Controller {
  static values = { recorded: Boolean, syncProgressUrl: String }

  async connect() {
    if (this.recordedValue) return
    if (!isStandalone()) return

    await logPwaInstall("standalone")
    await this.#syncProgress()
  }

  /**
   * Best-effort fetch of the current progress banner via Turbo Stream. A
   * no-op if the endpoint is absent or the request fails -- the next full
   * page load still reflects the real state anyway.
   */
  async #syncProgress() {
    if (!this.hasSyncProgressUrlValue) return

    try {
      await post(this.syncProgressUrlValue, { responseKind: "turbo-stream" })
    } catch {
      // Best-effort -- the next full page load reflects the real state anyway
    }
  }
}
